import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "./AuthContext";
import { AlertTriangle, Send, Loader2 } from "lucide-react";

export function useJobIdCheck(clientId) {
  const [check, setCheck] = useState(null);
  const [ack, setAck] = useState(false);
  useEffect(() => {
    setCheck(null); setAck(false);
    if (!clientId) return;
    axios.get(`${API_BASE}/voi/clients/${clientId}/job-check`).then((r) => setCheck(r.data)).catch(() => {});
  }, [clientId]);
  return { check, setCheck, ack, setAck, blocked: !!check?.warn && !ack && !check?.open_check_status };
}

export default function JobIdCheckWarning({ clientId, state }) {
  const { check, setCheck, ack, setAck } = state;
  const [busy, setBusy] = useState(false);
  if (!check?.warn) return null;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/voi/sessions`, { client_id: clientId, channels: ["email"] });
      if (data.delivery?.email?.ok) toast.success("ID check link emailed");
      else toast.warning("ID check created — email couldn't be delivered; copy the link from the client's ID check tab");
      setCheck({ ...check, open_check_status: "PENDING_UPLOAD" });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const why = check.reason === "new_client" ? "This is their first job" : "This client is flagged for an ID check";
  return (
    <div className="rounded-md border p-3 text-[12px] space-y-2" style={{ borderColor: "#B45309", background: "#FFFBEB" }}
         data-testid="job-id-check-warning">
      <div className="flex gap-2 text-[#92400E]">
        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
        <div>
          <b>{check.client_name} hasn't been ID verified.</b> {why} — the ATO/TPB expects identity to be verified before lodging.
          {check.open_check_status && <div className="mt-1" data-testid="job-id-check-open">An ID check is already in progress ({check.open_check_status === "PENDING_UPLOAD" ? "waiting for client" : "ready to review"}).</div>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {!check.open_check_status && (
          <button type="button" className="btn-secondary" onClick={send} disabled={busy} data-testid="job-id-check-send">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send ID check now
          </button>
        )}
        <label className="inline-flex items-center gap-1.5 cursor-pointer text-[var(--ink)]">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="job-id-check-ack" />
          Continue creating the job anyway
        </label>
      </div>
    </div>
  );
}
